import { auth } from "../firebaseConfig";
import {
  LEARNING_CONTENT_API_BASE_URL,
  LearningContentApiError,
} from "./learningContentApi";
import {
  LEARNING_CONTENT_SCHEMA_VERSION,
  LEGACY_LEARNING_CONTENT_SCHEMA_VERSION,
  MAX_IMAGE_ALT_TEXT_LENGTH,
  MAX_IMAGE_CAPTION_LENGTH,
  MAX_LEARNING_CONTENT_BLOCKS,
  MAX_RESOURCE_LABEL_LENGTH,
  isPlainObject,
  normalizeLearningContentBlock,
} from "./learningContent";

const BLOCK_ID_PATTERN = /^[A-Za-z0-9_-]+$/;
const LEARNING_CONTENT_VERSION_PATTERN = /^v1_[a-f0-9]{64}$/;
const MEDIA_CONTENT_TYPES = new Set([
  "image/jpeg",
  "image/png",
  "image/webp",
  "application/pdf",
]);

export class StudentLearningContentApiError extends LearningContentApiError {
  constructor(message, status = 0, code = "student-learning-content-unavailable") {
    super(message, status);
    this.name = "StudentLearningContentApiError";
    this.code = code;
  }
}

const exactFields = (value, fields) =>
  isPlainObject(value) &&
  Object.keys(value).length === fields.length &&
  fields.every((field) => Object.prototype.hasOwnProperty.call(value, field));

const validIdentifier = (value, maximumLength = 1500) =>
  typeof value === "string" &&
  value.length > 0 &&
  value === value.trim() &&
  value.length <= maximumLength &&
  !value.includes("/") &&
  value !== "." &&
  value !== "..";

const validIsoTimestamp = (value) => {
  if (typeof value !== "string") return false;
  const timestamp = new Date(value);
  return Number.isFinite(timestamp.getTime()) && timestamp.toISOString() === value;
};

export const normalizeStudentLearningContentAccess = (access) => {
  if (!isPlainObject(access)) {
    throw new StudentLearningContentApiError("A valid Learning Content access context is required.", 400, "invalid-access");
  }
  if (access.type === "assignment" && exactFields(access, ["type", "assignmentId"])) {
    if (!validIdentifier(access.assignmentId)) {
      throw new StudentLearningContentApiError("The assignment access context is invalid.", 400, "invalid-access");
    }
    return { type: "assignment", assignmentId: access.assignmentId };
  }
  if (
    access.type === "quiz-code" &&
    exactFields(access, ["type", "teacherUid", "quizCode"]) &&
    validIdentifier(access.teacherUid, 128) &&
    typeof access.quizCode === "string" &&
    access.quizCode.length > 0 &&
    access.quizCode === access.quizCode.trim() &&
    access.quizCode.length <= 256
  ) {
    return {
      type: "quiz-code",
      teacherUid: access.teacherUid,
      quizCode: access.quizCode,
    };
  }
  throw new StudentLearningContentApiError("The Learning Content access context is invalid.", 400, "invalid-access");
};

const requiredText = (value, maximumLength, name) => {
  if (
    typeof value !== "string" ||
    !value.trim() ||
    value !== value.trim() ||
    value.length > maximumLength ||
    /<\/?[a-z][^>]*>/i.test(value)
  ) {
    throw new StudentLearningContentApiError(
      `The server returned invalid ${name}.`,
      502,
      "invalid-response"
    );
  }
  return value;
};

const optionalText = (value, maximumLength, name) => {
  if (
    typeof value !== "string" ||
    value !== value.trim() ||
    value.length > maximumLength ||
    /<\/?[a-z][^>]*>/i.test(value)
  ) {
    throw new StudentLearningContentApiError(
      `The server returned invalid ${name}.`,
      502,
      "invalid-response"
    );
  }
  return value;
};

const normalizeDeliveredBlock = (block, index, schemaVersion) => {
  try {
    if (!isPlainObject(block) || !validIdentifier(block.id, 128) || !BLOCK_ID_PATTERN.test(block.id)) {
      throw new Error("invalid block id");
    }
    if (["heading", "text", "link"].includes(block.type)) {
      return normalizeLearningContentBlock(block, index);
    }
    if (schemaVersion !== LEARNING_CONTENT_SCHEMA_VERSION) {
      throw new Error("media requires schema version 2");
    }
    if (block.type === "video" && exactFields(block, ["id", "type", "title", "url"])) {
      return normalizeLearningContentBlock(block, index);
    }
    if (block.type === "image" && exactFields(block, ["id", "type", "altText", "caption"])) {
      return {
        id: block.id,
        type: "image",
        altText: requiredText(block.altText, MAX_IMAGE_ALT_TEXT_LENGTH, "image alt text"),
        caption: optionalText(block.caption, MAX_IMAGE_CAPTION_LENGTH, "image caption"),
      };
    }
    if (block.type === "resource" && exactFields(block, ["id", "type", "label"])) {
      return {
        id: block.id,
        type: "resource",
        label: requiredText(block.label, MAX_RESOURCE_LABEL_LENGTH, "resource label"),
      };
    }
    throw new Error("unsupported block");
  } catch (_error) {
    throw new StudentLearningContentApiError(
      "The server returned malformed Learning Content.",
      502,
      "invalid-response"
    );
  }
};

export const normalizeStudentLearningContentResponse = (value) => {
  if (
    !isPlainObject(value) ||
    value.ok !== true ||
    !exactFields(value, ["ok", "quiz", "learningContent"]) ||
    !exactFields(value.quiz, ["id", "title"]) ||
    !validIdentifier(value.quiz.id) ||
    typeof value.quiz.title !== "string" ||
    !value.quiz.title.trim() ||
    !exactFields(value.learningContent, [
      "schemaVersion",
      "revision",
      "learningContentVersion",
      "blocks",
    ])
  ) {
    throw new StudentLearningContentApiError(
      "The server returned an invalid Learning Content response.",
      502,
      "invalid-response"
    );
  }
  const { schemaVersion, revision, learningContentVersion, blocks } = value.learningContent;
  if (
    ![LEGACY_LEARNING_CONTENT_SCHEMA_VERSION, LEARNING_CONTENT_SCHEMA_VERSION].includes(schemaVersion) ||
    !Number.isSafeInteger(revision) ||
    revision < 1 ||
    typeof learningContentVersion !== "string" ||
    !LEARNING_CONTENT_VERSION_PATTERN.test(learningContentVersion) ||
    !Array.isArray(blocks) ||
    blocks.length < 1 ||
    blocks.length > MAX_LEARNING_CONTENT_BLOCKS
  ) {
    throw new StudentLearningContentApiError(
      "The server returned an invalid Learning Content response.",
      502,
      "invalid-response"
    );
  }
  const normalizedBlocks = blocks.map((block, index) =>
    normalizeDeliveredBlock(block, index, schemaVersion)
  );
  if (new Set(normalizedBlocks.map((block) => block.id)).size !== normalizedBlocks.length) {
    throw new StudentLearningContentApiError(
      "The server returned duplicate Learning Content blocks.",
      502,
      "invalid-response"
    );
  }
  return {
    quiz: { id: value.quiz.id, title: value.quiz.title.trim() },
    learningContent: {
      schemaVersion,
      revision,
      learningContentVersion,
      blocks: normalizedBlocks,
    },
  };
};

const authenticatedRequest = async (path, body, { signal } = {}) => {
  const currentUser = auth.currentUser;
  if (!currentUser) {
    throw new StudentLearningContentApiError(
      "Please sign in again to view Learning Content.",
      401,
      "authentication-required"
    );
  }
  const token = await currentUser.getIdToken();
  return fetch(`${LEARNING_CONTENT_API_BASE_URL}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(body),
    signal,
  });
};

const errorFromResponse = async (response, fallback) => {
  const data = await response.json().catch(() => ({}));
  const error = new StudentLearningContentApiError(
    data.error || fallback,
    response.status,
    data.code || "student-learning-content-unavailable"
  );
  if (Number.isSafeInteger(data.currentRevision) && data.currentRevision >= 1) {
    error.currentRevision = data.currentRevision;
  }
  return error;
};

export const normalizeStudentLearningContentCompletionResponse = (value) => {
  if (!isPlainObject(value) || value.ok !== true || typeof value.status !== "string") {
    throw new StudentLearningContentApiError(
      "The server returned an invalid Learning Content completion response.",
      502,
      "invalid-response"
    );
  }
  if (value.status === "not-required") {
    if (!exactFields(value, ["ok", "status", "quizId"]) || !validIdentifier(value.quizId)) {
      throw new StudentLearningContentApiError(
        "The server returned an invalid Learning Content completion response.",
        502,
        "invalid-response"
      );
    }
    return { status: "not-required", quizId: value.quizId };
  }
  const completion = value.completion;
  if (
    value.status !== "completed" ||
    !exactFields(value, ["ok", "status", "completion"]) ||
    !exactFields(completion, [
      "quizId",
      "learningContentRevision",
      "learningContentVersion",
      "completedAt",
      "alreadyCompleted",
    ]) ||
    !validIdentifier(completion.quizId) ||
    !Number.isSafeInteger(completion.learningContentRevision) ||
    completion.learningContentRevision < 1 ||
    completion.learningContentRevision >= Number.MAX_SAFE_INTEGER ||
    typeof completion.learningContentVersion !== "string" ||
    !LEARNING_CONTENT_VERSION_PATTERN.test(completion.learningContentVersion) ||
    !validIsoTimestamp(completion.completedAt) ||
    typeof completion.alreadyCompleted !== "boolean"
  ) {
    throw new StudentLearningContentApiError(
      "The server returned an invalid Learning Content completion response.",
      502,
      "invalid-response"
    );
  }
  return {
    status: "completed",
    completion: { ...completion },
  };
};

export const fetchStudentLearningContent = async (access, options = {}) => {
  const normalizedAccess = normalizeStudentLearningContentAccess(access);
  const response = await authenticatedRequest(
    "/api/learning-content/student",
    { access: normalizedAccess },
    options
  );
  if (!response.ok) {
    throw await errorFromResponse(response, "Learning Content could not be loaded.");
  }
  const data = await response.json().catch(() => null);
  return normalizeStudentLearningContentResponse(data);
};

export const completeStudentLearningContent = async (
  access,
  expectedRevision,
  expectedLearningContentVersion,
  options = {}
) => {
  const normalizedAccess = normalizeStudentLearningContentAccess(access);
  if (
    !Number.isSafeInteger(expectedRevision) ||
    expectedRevision < 1 ||
    expectedRevision >= Number.MAX_SAFE_INTEGER
  ) {
    throw new StudentLearningContentApiError(
      "The Learning Content revision is invalid.",
      400,
      "invalid-revision"
    );
  }
  if (
    typeof expectedLearningContentVersion !== "string" ||
    !LEARNING_CONTENT_VERSION_PATTERN.test(expectedLearningContentVersion)
  ) {
    throw new StudentLearningContentApiError(
      "The Learning Content version is invalid.",
      400,
      "invalid-learning-content-version"
    );
  }
  const response = await authenticatedRequest(
    "/api/learning-content/student/complete",
    {
      access: normalizedAccess,
      expectedRevision,
      expectedLearningContentVersion,
    },
    options
  );
  if (!response.ok) {
    throw await errorFromResponse(response, "Learning Content progress could not be saved.");
  }
  const data = await response.json().catch(() => null);
  const normalized = normalizeStudentLearningContentCompletionResponse(data);
  if (
    normalized.status === "completed" &&
    (normalized.completion.learningContentRevision !== expectedRevision ||
      normalized.completion.learningContentVersion !== expectedLearningContentVersion)
  ) {
    throw new StudentLearningContentApiError(
      "The server returned conflicting Learning Content completion metadata.",
      502,
      "invalid-response"
    );
  }
  return normalized;
};

export const fetchStudentLearningContentMedia = async (
  access,
  blockId,
  revision,
  options = {}
) => {
  const normalizedAccess = normalizeStudentLearningContentAccess(access);
  if (!validIdentifier(blockId, 128) || !BLOCK_ID_PATTERN.test(blockId)) {
    throw new StudentLearningContentApiError("The Learning Content media block is invalid.", 400, "invalid-block-id");
  }
  if (!Number.isSafeInteger(revision) || revision < 1) {
    throw new StudentLearningContentApiError("The Learning Content revision is invalid.", 400, "invalid-revision");
  }
  const response = await authenticatedRequest(
    "/api/learning-content/student/media",
    { access: normalizedAccess, blockId, revision },
    options
  );
  if (!response.ok) {
    throw await errorFromResponse(response, "Learning Content media could not be loaded.");
  }
  const contentType = String(response.headers?.get?.("content-type") || "")
    .split(";", 1)[0]
    .trim()
    .toLowerCase();
  if (!MEDIA_CONTENT_TYPES.has(contentType)) {
    throw new StudentLearningContentApiError(
      "The server returned an unsupported Learning Content media type.",
      502,
      "invalid-media-response"
    );
  }
  const blob = await response.blob();
  if (!blob || blob.size < 1 || blob.type.toLowerCase() !== contentType) {
    throw new StudentLearningContentApiError(
      "The server returned invalid Learning Content media.",
      502,
      "invalid-media-response"
    );
  }
  return blob;
};
